import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Globe2, MapPin, Building2, ChevronRight, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import AdminUsersMap from "@/components/AdminUsersMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const num = (v) => Number(v || 0).toLocaleString("pt-BR");

function Bars({ rows, total, onPick, testPrefix, color }) {
  const max = rows[0]?.count || 1;
  return (
    <ul className="space-y-1.5" data-testid={`${testPrefix}-list`}>
      {rows.slice(0, 12).map((r) => (
        <li key={r.name}>
          <button
            type="button"
            onClick={() => onPick?.(r.name)}
            disabled={!onPick || r.name === "Não informado"}
            data-testid={`${testPrefix}-${r.name}`}
            className="w-full text-left group disabled:cursor-default"
          >
            <div className="flex items-center justify-between text-xs mb-0.5">
              <span className="font-medium truncate group-hover:text-primary flex items-center gap-1">{r.name}{onPick && r.name !== "Não informado" && <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100" />}</span>
              <span className="tabular-nums text-muted-foreground">{num(r.count)} · {total ? ((r.count / total) * 100).toFixed(1) : 0}%</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${Math.max(2, (r.count / max) * 100)}%` }} />
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Admin: signups by country / state / city with drill-down filters. `onFilter` pushes the selection to the users list. */
export default function AdminGeoStats({ onFilter }) {
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (country) params.set("country", country);
    if (state) params.set("state", state);
    axios.get(`${API}/admin/users/geo-stats?${params}`).then((r) => setData(r.data)).catch(() => toast.error("Erro ao carregar distribuição geográfica"));
  }, [country, state]);

  useEffect(() => { onFilter?.({ country, state, city: "" }); }, [country, state, onFilter]);

  if (!data) return null;
  return (
    <Card className="p-5 space-y-4" data-testid="admin-geo-stats">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-bold tracking-tight flex items-center gap-2"><Globe2 className="w-4 h-4 text-primary" /> Cadastros por localização</h3>
          <p className="text-xs text-muted-foreground">Clique em um país ou estado para detalhar; o filtro também se aplica à lista de usuários abaixo.</p>
        </div>
        <div className="flex items-center gap-2 text-xs flex-wrap" data-testid="admin-geo-breadcrumb">
          <button type="button" onClick={() => { setCountry(""); setState(""); }} className={`px-2 py-1 rounded-full ${!country ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/70"}`} data-testid="admin-geo-crumb-all">Todos</button>
          {country && <><ChevronRight className="w-3 h-3" /><button type="button" onClick={() => setState("")} className={`px-2 py-1 rounded-full ${!state ? "bg-primary text-primary-foreground" : "bg-muted"}`} data-testid="admin-geo-crumb-country">{country}</button></>}
          {state && <><ChevronRight className="w-3 h-3" /><span className="px-2 py-1 rounded-full bg-primary text-primary-foreground" data-testid="admin-geo-crumb-state">{state}</span></>}
          {(country || state) && <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setCountry(""); setState(""); }} data-testid="admin-geo-clear"><X className="w-3 h-3" /></Button>}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="p-3 rounded-xl border bg-card" data-testid="admin-geo-total"><div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Cadastros{country ? " no filtro" : ""}</div><div className="text-xl font-bold">{num(data.total)}</div></div>
        <div className="p-3 rounded-xl border bg-card" data-testid="admin-geo-with"><div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Com localização</div><div className="text-xl font-bold text-emerald-600">{num(data.with_location)}</div></div>
        <div className="p-3 rounded-xl border bg-card" data-testid="admin-geo-without"><div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Sem localização</div><div className="text-xl font-bold text-amber-600">{num(data.without_location)}</div></div>
      </div>
      <AdminUsersMap />
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1"><Globe2 className="w-3 h-3" /> Países ({data.countries.length})</div>
          <Bars rows={data.countries} total={data.total} onPick={(n) => { setCountry(n); setState(""); }} testPrefix="admin-geo-country" color="bg-primary" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> Estados ({data.states.length})</div>
          <Bars rows={data.states} total={data.total} onPick={(n) => setState(n)} testPrefix="admin-geo-state" color="bg-cyan-500" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1"><Building2 className="w-3 h-3" /> Cidades ({data.cities.length})</div>
          <Bars rows={data.cities} total={data.total} onPick={(n) => onFilter?.({ country, state, city: n })} testPrefix="admin-geo-city" color="bg-orange-500" />
        </div>
      </div>
    </Card>
  );
}
